/**
 * Where this deal's rent sits against its class comp band.
 *
 * ⚠️ WHY IT IS RENDERED AND NOT LEFT TO THE NARRATIVE. #517 put this fact in front of the Scorecard
 * model, which was the right first move and an incomplete one: a reader then sees only whatever the
 * model chose to say about it in prose, and this codebase has already caught a model's prose
 * contradicting its own structured output on the same run. The figure is deterministic, so it is shown.
 *
 * ⚠️ THE BASIS IS PART OF THE CLAIM, not a footnote. On 14 of 75 stored deals the rent is DERIVED from
 * gross potential rent rather than stated — and GPR assumes full occupancy with no concessions, while
 * the comp band is asking rent. When the comps also report a market-wide concession level (26.7% on the
 * first real comp set), an above-band reading is a FLOOR on the gap, not a measurement of it. Both
 * qualifiers render; neither is hidden behind a tooltip.
 *
 * It reports a POSITION, never a verdict. A comp set can be stale, mis-classed, or from another
 * submarket, and the deal may not be the class its sponsor calls it.
 *
 * 🔴 ADR-0043 R2 — SAY WHICH REFERENCE PRODUCED THE POSITION. The band is the comparables EXTRACTED FROM
 * THIS DEAL'S OWN DOCUMENTS: a comp set in an OM, or an appraisal the package happened to include. The
 * sponsor chose what went in the package. The card used to title that "Rent vs the market", which
 * presented the sponsor's own evidence as an independent reference. It now names the source. An
 * independently-sourced reference beside it is the rest of R2 and a separate decision (HUD FMR is a
 * voucher benchmark near the 40th percentile of gross rents, not a market comp for a Class A deal).
 */

import type { RentPositionView } from '../api.js';
import { percentFromRatio } from '../format.js';

/** `$1,730` — per-unit monthly rents read better whole; the cents are noise at this scale. */
function perUnit(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `$${Math.round(value).toLocaleString('en-US')}`;
}

const STANDING_LABEL: Record<string, string> = {
  above: 'above the band',
  within: 'within the band',
  below: 'below the band',
};

export function RentPositionCard({ position }: { position: RentPositionView }) {
  const { bandLow, bandHigh, dealRent, standing, outsideBy, concessionRate } = position;
  const outside = standing === 'above' || standing === 'below';
  const derived = position.basis === 'derived-from-gpr';

  return (
    <section className="card">
      <h3 className="card__title">
        Rent vs the package&rsquo;s comparables
        {standing && (
          <span className={`pill pill--badge rentpos__pill rentpos__pill--${standing}`}>
            {STANDING_LABEL[standing] ?? standing}
          </span>
        )}
      </h3>

      <p className="card__prose">
        This deal&rsquo;s rent against the comparable properties stated in its own documents
        {position.canonicalClass ? ` for Class ${position.canonicalClass}` : ''}.
      </p>
      <p className="card__note">
        These comparables come from the deal package, which the sponsor assembled. They are not an
        independent market series, so read the band as the package&rsquo;s own evidence.
      </p>

      <div className="rentpos">
        <div className="rentpos__fig">
          <span className="rentpos__val">{perUnit(dealRent)}</span>
          <span className="rentpos__cap">this deal, per unit/month</span>
        </div>
        <div className="rentpos__fig">
          <span className="rentpos__val">
            {perUnit(bandLow)}–{perUnit(bandHigh)}
          </span>
          <span className="rentpos__cap">comparable band</span>
        </div>
        {outside && outsideBy ? (
          <div className="rentpos__fig rentpos__fig--delta">
            <span className="rentpos__val">{perUnit(outsideBy)}</span>
            <span className="rentpos__cap">
              {standing === 'above' ? 'above the ceiling' : 'below the floor'}
            </span>
          </div>
        ) : null}
      </div>

      {derived && (
        <p className="card__note">
          The deal&rsquo;s rent is <strong>derived</strong> from gross potential rent ÷ units ÷ 12 —
          the documents do not state an average rent. That is a full-occupancy figure before any
          concession.
        </p>
      )}

      {concessionRate !== null && concessionRate !== undefined && (
        <p className="card__note">
          The comparable set reports market-wide concessions of{' '}
          <strong>{percentFromRatio(concessionRate, 1)}</strong>. Comparing a gross rent to
          asking-rent comparables understates any gap, so treat the figures above as a floor rather
          than a measurement.
        </p>
      )}

      {/* Only when the deal IS outside. On an in-band deal this caveat answers a question nobody
          asked and quietly invites the reader to wonder what was found. Caught in the preview, not
          by the SSR tests — which asserted the line renders, never that it renders only when apt. */}
      {outside && (
        <p className="card__note">
          A rent outside the band is a question, not a finding: comparable sets go stale, and a
          property is not always the class its sponsor assigns it.
        </p>
      )}
    </section>
  );
}
